import {get, orderBy, round} from 'lodash';
import React, {
  CSSProperties,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import {useDispatch} from 'react-redux';
import swal from 'sweetalert2';
import {getMPPEndpoint, getHeaders} from '../../../../../helpers/endpoint';
import {INotificationGet, INotificationsStat} from '../../../../../types/notifications';

interface NotificationsListProps {}

export const notificationsStatus = {
  1: 'Created',
  2: 'Scheduled',
  3: 'Sending',
  4: 'Sent',
};

const Styles: Record<string, CSSProperties> = {
  container: {
    margin: 16,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 16,
    paddingBottom: 16,
    width: '100%',
    backgroundColor: 'white',
  },
};

export const getAllBlogPosts = (
  onSuccess: (response: INotificationGet[]) => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getMPPEndpoint('getAllBlogPosts'), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data.blogPosts);
        }
      })
      .catch(() => {});
  };
};

export const deleteBlogPostById = (
  blogPostId: string,
  onSuccess: () => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getMPPEndpoint('deleteBlogPostById', blogPostId), {
      method: 'DELETE',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          // eslint-disable-next-line no-unused-expressions
          onSuccess?.();
        }
      })
      .catch(() => {});
  };
};

export const getBlogPostsStats = (
  onSuccess: (response: INotificationsStat) => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getMPPEndpoint('getBlogPostsStats'), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data.stats);
        }
      })
      .catch(() => {});
  };
};

export type OrderType = {type: string; sort: 'asc' | 'desc'};

const orderNotifications = (
  notifications: INotificationGet[],
  order: OrderType,
  notificationsStats: INotificationsStat | undefined,
) => {
  if (order.type === 'name' || order.type === 'status') {
    return orderBy(notifications, [order.type], [order.sort]);
  } else {
    return orderBy(
      notifications,
      [
        notif => {
          return notif.id && get(notificationsStats?.[notif.id], order.type);
        },
      ],
      [order.sort],
    );
  }
};

export const MPPNotificationsList: FunctionComponent<NotificationsListProps> = ({
  history,
}) => {
  const dispatch = useDispatch();
  const [order, setOrder] = useState<OrderType>({
    type: 'status',
    sort: 'asc',
  });
  const [notifications, setNotifications] = useState<INotificationGet[]>();
  const [
    notificationsStats,
    setNotificationsStats,
  ] = useState<INotificationsStat>();

  useEffect(() => {
    dispatch(getBlogPostsStats(response => setNotificationsStats(response)));
    dispatch(
      getAllBlogPosts(response =>
        setNotifications(
          orderNotifications(response, order, notificationsStats),
        ),
      ),
    );
  }, []);

  useEffect(() => {
    if (notifications) {
      setNotifications(
        orderNotifications(notifications, order, notificationsStats),
      );
    }
  }, [order]);

  const setOrderForNotif = (type: string) => {
    setOrder(state => {
      return {
        type,
        sort:
          state.type === type
            ? state.sort === 'asc'
              ? 'desc'
              : 'asc'
            : state.sort,
      };
    });
  };

  return (
    <div style={Styles.container}>
      <table className="table table-responsive table-striped text-center">
        <thead>
          <tr>
            <th
              onClick={() => setOrderForNotif('name')}
              style={{cursor: 'pointer'}}>
              Name
            </th>
            <th
              onClick={() => setOrderForNotif('all.sent')}
              style={{cursor: 'pointer'}}>
              Sent
            </th>
            <th
              onClick={() => setOrderForNotif('all.viewed')}
              style={{cursor: 'pointer'}}>
              Views
            </th>
            <th
              onClick={() => setOrderForNotif('all.clicked')}
              style={{cursor: 'pointer'}}>
              Clicks
            </th>
            <th
              onClick={() => setOrderForNotif('uniq.viewed')}
              style={{cursor: 'pointer'}}>
              Unique Views
            </th>
            <th>Open rate</th>
            <th
              onClick={() => setOrderForNotif('uniq.clicked')}
              style={{cursor: 'pointer'}}>
              Unique Clicks
            </th>
            <th>Reactivity rate</th>
            <th
              onClick={() => setOrderForNotif('status')}
              style={{cursor: 'pointer'}}>
              Status
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {notifications?.map(notif => {
            const stats = notif.id ? notificationsStats?.[notif.id] : undefined;
            return (
              <tr key={notif.id}>
                <th>{notif.name}</th>
                <th>{stats?.all.sent || '-'}</th>
                <th>{stats?.all.viewed || '-'}</th>
                <th>{stats?.all.clicked || '-'}</th>
                <th>{stats?.uniq.viewed || '-'}</th>
                <th>
                  {(stats?.all.sent &&
                    stats?.uniq.viewed &&
                    `${round(
                      (stats?.uniq.viewed / stats?.all.sent) * 100,
                      2,
                    )}%`) ||
                    '-'}
                </th>
                <th>{stats?.uniq.clicked || '-'}</th>
                <th>
                  {(stats?.uniq.viewed &&
                    stats?.uniq.clicked &&
                    `${round(
                      (stats?.uniq.clicked / stats?.uniq.viewed) * 100,
                      2,
                    )}%`) ||
                    '-'}
                </th>
                <th>{notif.status && notificationsStatus[notif.status]}</th>
                <th>
                  <button
                    className="btn"
                    onClick={() =>
                      history.push(`/mpp/notification/update/${notif.id}`)
                    }>
                    Modifier
                  </button>
                  <button
                    style={{marginLeft: 8, marginRight: 8}}
                    className="btn"
                    onClick={() =>
                      history.push(
                        `/mpp/notification/create?blogPostId=${notif.id}`,
                      )
                    }>
                    <i className="fa fa-clone"></i>
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      swal({
                        title: 'Are you sure ?',
                        type: 'info',
                        showCloseButton: true,
                        showCancelButton: true,
                        focusConfirm: false,
                        confirmButtonText: 'Yes',
                        cancelButtonText: 'No',
                      }).then(() => {
                        if (notif.id) {
                          dispatch(
                            deleteBlogPostById(notif.id, () =>
                              dispatch(
                                getAllBlogPosts(response =>
                                  setNotifications(response),
                                ),
                              ),
                            ),
                          );
                        }
                      });
                    }}>
                    <i style={{color: 'white'}} className="fa fa-trash" />
                  </button>
                </th>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/Notifications/List/index.tsx